import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Add as AddIcon,
  Link as LinkIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../context/AuthContext';
import { SignupFormData } from '../../types/auth.types';
import EmailVerificationRequired from './EmailVerificationRequired';

// Validation schema
const signupSchema = z
  .object({
    displayName: z.string().min(2, 'Name must be at least 2 characters'),
    email: z.string().email('Invalid email address'),
    organizationName: z.string().min(2, 'Organization name must be at least 2 characters'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SignupMode = 'select' | 'create' | 'join';

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const navigate = useNavigate();
  const { signup, pendingVerification, clearPendingVerification } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [mode, setMode] = useState<SignupMode>('select');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  // Extract token from invite link
  function extractTokenFromLink(link: string): string | null {
    try {
      // Handle full URLs like https://example.com/invite/abc123
      if (link.includes('/invite/')) {
        const parts = link.split('/invite/');
        return parts[1]?.split(/[?#]/)[0] || null;
      }
      // Handle just the token (64 character hex string)
      if (link.length === 64 && /^[a-f0-9]+$/.test(link)) {
        return link;
      }
      return null;
    } catch {
      return null;
    }
  }

  const onSubmit = async (data: SignupFormData) => {
    try {
      setError('');
      setIsLoading(true);
      setRegisteredEmail(data.email); // Store email for verification screen
      await signup(data.email, data.password, data.displayName, data.organizationName);
      enqueueSnackbar('Account created! Please verify your email.', { variant: 'success' });
      // Don't navigate - show verification screen instead
      setIsLoading(false);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create account';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setRegisteredEmail(null);
      setIsLoading(false);
    }
  };

  function handleJoinOrganization() {
    const token = extractTokenFromLink(inviteLink.trim());

    if (!token) {
      setError('Please enter a valid invitation link or code');
      return;
    }

    // Navigate to the invite page with the extracted token
    navigate(`/invite/${token}`);
  }

  // If pending verification after signup, show the verification screen
  if (pendingVerification && registeredEmail) {
    return (
      <EmailVerificationRequired
        email={registeredEmail}
        onVerified={() => {
          clearPendingVerification();
          setRegisteredEmail(null);
          enqueueSnackbar('Email verified! Welcome to ShiftGrid.', { variant: 'success' });
          navigate('/dashboard');
        }}
        onBack={() => {
          clearPendingVerification();
          setRegisteredEmail(null);
        }}
      />
    );
  }

  // Mode: Select - Choose between Create or Join
  if (mode === 'select') {
    return (
      <Box>
        <Typography variant="h5" component="h2" gutterBottom align="center">
          Get Started
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Choose how you'd like to join ShiftGrid
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => setMode('join')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LinkIcon sx={{ color: 'white' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Join an Organization
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  I have an invitation link from my employer
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Divider>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => setMode('create')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AddIcon sx={{ color: 'grey.700' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Create New Organization
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  I want to set up ShiftGrid for my business
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={onSwitchToLogin}
              sx={{ cursor: 'pointer' }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </Box>
    );
  }

  // Mode: Join - Paste invitation link
  if (mode === 'join') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton
            onClick={() => {
              setMode('select');
              setInviteLink('');
              setError('');
            }}
            size="small"
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h2">
            Join an Organization
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Paste the invitation link you received from your organization
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Invitation Link"
          placeholder="https://... or paste the invite code"
          value={inviteLink}
          onChange={(e) => {
            setInviteLink(e.target.value);
            setError('');
          }}
          fullWidth
          margin="normal"
          helperText="Paste the full invitation link or just the invite code"
          autoFocus
        />

        <Button
          fullWidth
          variant="contained"
          size="large"
          sx={{ mt: 2, mb: 2 }}
          onClick={handleJoinOrganization}
          disabled={!inviteLink.trim()}
        >
          Continue
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an invitation?{' '}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => setMode('create')}
              sx={{ cursor: 'pointer' }}
            >
              Create your own organization
            </Link>
          </Typography>
        </Box>
      </Box>
    );
  }

  // Mode: Create - Full signup form
  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton
          onClick={() => {
            setMode('select');
            setError('');
          }}
          size="small"
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h2">
          Create Organization
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Set up a new organization and invite your team
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        {...register('displayName')}
        label="Your Full Name"
        type="text"
        fullWidth
        margin="normal"
        autoComplete="name"
        autoFocus
        error={!!errors.displayName}
        helperText={errors.displayName?.message}
        disabled={isLoading}
      />

      <TextField
        {...register('email')}
        label="Email Address"
        type="email"
        fullWidth
        margin="normal"
        autoComplete="email"
        error={!!errors.email}
        helperText={errors.email?.message}
        disabled={isLoading}
      />

      <TextField
        {...register('organizationName')}
        label="Organization Name"
        type="text"
        fullWidth
        margin="normal"
        autoComplete="organization"
        error={!!errors.organizationName}
        helperText={errors.organizationName?.message || 'Your company or shop name'}
        disabled={isLoading}
      />

      <TextField
        {...register('password')}
        label="Password"
        type={showPassword ? 'text' : 'password'}
        fullWidth
        margin="normal"
        autoComplete="new-password"
        error={!!errors.password}
        helperText={errors.password?.message}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        {...register('confirmPassword')}
        label="Confirm Password"
        type={showConfirmPassword ? 'text' : 'password'}
        fullWidth
        margin="normal"
        autoComplete="new-password"
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword?.message}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        sx={{ mt: 3, mb: 2 }}
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size={24} /> : 'Create Account'}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Have an invitation link?{' '}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setMode('join')}
            sx={{ cursor: 'pointer' }}
          >
            Join an organization
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
